import React, { useState, useEffect, useRef } from "react";
import {
  CheckCircleOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  ToolOutlined,
  CloudDownloadOutlined,
  MonitorOutlined,
  RadarChartOutlined,
  BarChartOutlined,
  NumberOutlined,
} from "@ant-design/icons";

function ProcessSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isZoomedIn, setIsZoomedIn] = useState(false);

  const sectionRef = useRef(null);

  const carouselItems = [
    {
      icon: <ThunderboltOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Line Monitoring",
      description:
        "Real-time tracking ensures efficiency, quality, workflow-optimization and quick issue identification for productivity.",
    },
    {
      icon: <RadarChartOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Machine Performance Monitoring",
      description:
        "Track operational metrics such as speed, efficiency, and downtime to optimize productivity.",
    },
    {
      icon: <BarChartOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Predictive Maintenance",
      description:
        "Forecast equipment failures before they occur, minimizing unexpected downtime and enhancing reliability and reduces overall maintenance costs.",
    },
  ];

  const carouselItemsTwo = [
    {
      icon: <DatabaseOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Incident Resolution",
      description:
        "Offers troubleshooting guidance/assistance for operational issues, minimizing downtime and enhancing overall machine performance.",
    },
    {
      icon: <CloudDownloadOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Data Analytics",
      description:
        "Transforms raw performance metrics into actionable insights, enabling informed decision-making for optimization.",
    },
    {
      icon: <NumberOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Customised Industry 4.0 solutions",
      description:
        "Tailor advanced technologies like IoT, AI, and automation to meet specific industry needs, enhancing operational efficiency.",
    },
  ];

  const carouselItemsThree = [
    {
      icon: <CheckCircleOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Production Monitoring",
      description:
        "Ensure real-time tracking of output, product count to enable quick identification of bottlenecks.",
    },
    {
      icon: <ToolOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "Energy Monitoring",
      description:
        "Tracks power consumption in real time, providing insights into efficiency and operational costs",
    },
    {
      icon: <MonitorOutlined className="text-2xl xs:text-3xl sm:text-3xl md:text-4xl text-blue-500" />,
      title: "CNC Monitoring",
      description:
        "Real-time tracking of CNC machines enhances precision, productivity, and reduces errors through metric analysis.",
    },
  ];

  useEffect(() => {
    const handleNext = () => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselItems.length);
        setIsTransitioning(false);
        setProgress(0);
      }, 500);
    };

    if (!isPaused) {
      const interval = setInterval(() => {
        handleNext();
      }, 2000);

      const progressInterval = setInterval(() => {
        setProgress((prev) => (prev < 100 ? prev + 1 : 0));
      }, 25);

      return () => {
        clearInterval(interval);
        clearInterval(progressInterval);
      };
    }
  }, [currentIndex, isPaused, carouselItems.length]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    setProgress(0);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    setProgress(0);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsZoomedIn(true);
          } else {
            setIsZoomedIn(false);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className={`w-full px-4 xs:px-4 sm:px-6 md:px-8 lg:px-12 py-10 xs:py-10 sm:py-12 md:py-16 lg:py-20 bg-gray-100 dark:bg-gray-900 rounded-2xl my-10 xs:my-10 sm:my-12 md:my-16 transform transition-all duration-700 ${
        isZoomedIn ? "scale-100 opacity-100" : "scale-90 opacity-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-8 xs:gap-8 sm:gap-10 md:gap-12 lg:gap-16 items-center">
          
          {/* Left Content */}
          <div className="w-full md:w-1/2 space-y-6 xs:space-y-6 sm:space-y-8">
            <h2 className="text-3xl xs:text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
              Digitize Your <span className="text-orange-500">Processes</span> And
              Gain Analytics From Day One
            </h2>
            <p className="text-lg xs:text-xl sm:text-2xl md:text-3xl text-gray-600 dark:text-gray-400 leading-relaxed">
              Take a look at all the key <b>Processes</b> we cover. No Need To
              Design Or Develop Custom Software For Your Operations.
            </p>
          </div>

          {/* Right Carousel */}
          <div className="w-full md:w-1/2">
            <div
              className="relative w-full p-4 xs:p-6 sm:p-8 bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden cursor-pointer"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {/* First Item */}
              <div
                className={`flex flex-row-reverse items-start xs:items-center gap-3 xs:gap-4 sm:gap-5 mb-6 xs:mb-8 transition-all duration-500 ease-in-out transform ${
                  isTransitioning
                    ? "translate-x-full opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                <div className="flex-shrink-0">{carouselItems[currentIndex].icon}</div>
                <div className="flex-1">
                  <h3 className="text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-white text-end mb-2">
                    {carouselItems[currentIndex].title}
                  </h3>
                  <p className="text-xs xs:text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 text-end">
                    {carouselItems[currentIndex].description}
                  </p>
                </div>
              </div>

              {/* Second Item */}
              <div
                className={`flex flex-row items-start xs:items-center gap-3 xs:gap-4 sm:gap-5 mb-6 xs:mb-8 transition-all duration-500 ease-in-out transform ${
                  isTransitioning
                    ? "translate-x-[-100%] opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                <div className="flex-shrink-0">{carouselItemsTwo[currentIndex].icon}</div>
                <div className="flex-1">
                  <h3 className="text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-white text-start mb-2">
                    {carouselItemsTwo[currentIndex].title}
                  </h3>
                  <p className="text-xs xs:text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 text-start">
                    {carouselItemsTwo[currentIndex].description}
                  </p>
                </div>
              </div>

              {/* Third Item */}
              <div
                className={`flex flex-row-reverse items-start xs:items-center gap-3 xs:gap-4 sm:gap-5 mb-6 xs:mb-8 transition-all duration-500 ease-in-out transform ${
                  isTransitioning
                    ? "translate-x-full opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                <div className="flex-shrink-0">{carouselItemsThree[currentIndex].icon}</div>
                <div className="flex-1">
                  <h3 className="text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-white text-end mb-2">
                    {carouselItemsThree[currentIndex].title}
                  </h3>
                  <p className="text-xs xs:text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 text-end">
                    {carouselItemsThree[currentIndex].description}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-1 bg-gray-800 dark:bg-white transition-all duration-100"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessSection;
